import { mat3, vec3 } from 'gl-matrix';
import { WorldBoundsMm } from './gridGeometry';

/**
 * The extent a source volume occupies in patient space, as the
 * parallelepiped it actually is.
 *
 * An axis-aligned bounding box is only the same shape when the source is
 * transversal. For anything oblique the bounding box is strictly larger, and the
 * difference is not academic: it is the corner wedges, which a reformat
 * pads and a box-against-box measurement reports as covered.
 *
 * The edges are not assumed orthogonal — a stack whose positions step off
 * its own slice normal is a sheared parallelepiped, and this describes that
 * too.
 */
export interface OrientedBoxMm {
    /** Outer corner of the first voxel. */
    origin: vec3;
    /** Full edge vectors from that corner, one per volume axis. */
    edges: [vec3, vec3, vec3];
}

/**
 * A point's coordinates in the box's own frame, where the box is the unit
 * cube. Inside is every component in [0, 1].
 *
 * The inverse is taken once and reused across the many points a fraction
 * measurement tests.
 */
function boxFrame(box: OrientedBoxMm): mat3 | null {
    const m = mat3.fromValues(
        box.edges[0][0],
        box.edges[0][1],
        box.edges[0][2],
        box.edges[1][0],
        box.edges[1][1],
        box.edges[1][2],
        box.edges[2][0],
        box.edges[2][1],
        box.edges[2][2]
    );
    const inverse = mat3.create();
    // Degenerate for a box with no volume, which has nothing to be inside.
    return mat3.invert(inverse, m) === null ? null : inverse;
}

/**
 * How far outside the unit cube a corner may sit and still count as
 * inside, as a fraction of the box.
 *
 * A region built to match the source exactly comes back through a `Grid`,
 * which holds its resolution and transform in float32, so its corners land
 * a few parts in 10^8 of the extent outside the box they were built from.
 * Without an allowance those corners read as outside, the lattice measures
 * nothing, the floor applies, and a reformat containing no padding at all
 * reports "1% padded" — in the panel and in DerivationDescription both.
 *
 * 1e-5 of the box is five micrometres across a 50 cm field of view: two
 * orders of magnitude above the float32 residue it absorbs, and two below
 * the thinnest overhang worth reporting. (The residue is currently rounded
 * away by gl-matrix's own float32 storage, but only by a factor of five,
 * and that margin moves with the spacing.)
 */
const CORNER_TOLERANCE = 1e-5;

/** Number of samples per axis used to measure a covered fraction. */
const SAMPLES_PER_AXIS = 64;

/**
 * Smallest fraction of `output` reported as padding once any padding
 * exists.
 *
 * `paddedPercent` rounds a non-zero fraction UP to 1%, so any positive
 * value below that reports as "1% padded" — which is the honest statement
 * for an overhang the lattice is too coarse to measure.
 */
const MINIMUM_REPORTED_FRACTION = 1e-4;

/**
 * Fraction of `output` that lies outside `source`, in [0, 1] — how much of
 * the reformatted image will be padding rather than resampled data.
 *
 * Whether there is ANY padding is decided exactly, by testing the output's
 * eight corners: both shapes are convex, so a source containing every
 * corner contains the whole box. HOW MUCH is then estimated by sampling
 * `output` on a regular lattice, there being no closed form for the
 * intersection of a box with an arbitrarily oriented, possibly sheared one.
 *
 * The two are separate because the lattice cannot answer the first
 * question. Its error is on the order of one sample spacing at each face —
 * an absolute quantity, so it can miss a thin overhang entirely: 2 mm past
 * a 302 mm output measured 0.000%, which at 1 mm slices is two fabricated
 * slices reported as none. The exact test catches that case and the
 * estimate is floored above zero, so a padded output is never described as
 * unpadded.
 *
 * The lattice is the same every time, so the figure the user accepts and
 * the figure the output records agree exactly.
 */
export function paddedFractionInBox(
    output: WorldBoundsMm,
    source: OrientedBoxMm
): number {
    const spanX = output.maxX - output.minX;
    const spanY = output.maxY - output.minY;
    const spanZ = output.maxZ - output.minZ;
    if (spanX <= 0 || spanY <= 0 || spanZ <= 0) {
        return 0;
    }

    const inverse = boxFrame(source);
    if (inverse === null) {
        return 1;
    }

    if (cornersInside(output, source, inverse)) {
        return 0;
    }

    const n = SAMPLES_PER_AXIS;
    const rel = vec3.create();
    const local = vec3.create();
    let inside = 0;
    for (let k = 0; k < n; k++) {
        const z = output.minZ + ((k + 0.5) / n) * spanZ;
        for (let j = 0; j < n; j++) {
            const y = output.minY + ((j + 0.5) / n) * spanY;
            for (let i = 0; i < n; i++) {
                const x = output.minX + ((i + 0.5) / n) * spanX;
                vec3.set(
                    rel,
                    x - source.origin[0],
                    y - source.origin[1],
                    z - source.origin[2]
                );
                vec3.transformMat3(local, rel, inverse);
                if (
                    local[0] >= 0 &&
                    local[0] <= 1 &&
                    local[1] >= 0 &&
                    local[1] <= 1 &&
                    local[2] >= 0 &&
                    local[2] <= 1
                ) {
                    inside++;
                }
            }
        }
    }

    return Math.max(MINIMUM_REPORTED_FRACTION, 1 - inside / (n * n * n));
}

/** Whether every corner of `output` lies in `source`. */
function cornersInside(
    output: WorldBoundsMm,
    source: OrientedBoxMm,
    inverse: mat3
): boolean {
    const rel = vec3.create();
    const local = vec3.create();
    for (const x of [output.minX, output.maxX]) {
        for (const y of [output.minY, output.maxY]) {
            for (const z of [output.minZ, output.maxZ]) {
                vec3.set(
                    rel,
                    x - source.origin[0],
                    y - source.origin[1],
                    z - source.origin[2]
                );
                vec3.transformMat3(local, rel, inverse);
                if (
                    local[0] < -CORNER_TOLERANCE ||
                    local[0] > 1 + CORNER_TOLERANCE ||
                    local[1] < -CORNER_TOLERANCE ||
                    local[1] > 1 + CORNER_TOLERANCE ||
                    local[2] < -CORNER_TOLERANCE ||
                    local[2] > 1 + CORNER_TOLERANCE
                ) {
                    return false;
                }
            }
        }
    }
    return true;
}

/**
 * Whether `output` meets `source` at all.
 *
 * Separate from {@link paddedFractionInBox} because a refusal must not rest
 * on a sampled figure: a region clipping one corner of the source is a
 * region the user can still work with, and a lattice coarse enough to be
 * cheap can miss it entirely. This is exact — the separating axis theorem
 * over both shapes' face normals and their edge cross products — so an
 * intersection of any size is found.
 */
export function boxMeetsBounds(
    source: OrientedBoxMm,
    output: WorldBoundsMm
): boolean {
    const outputEdges: vec3[] = [
        vec3.fromValues(output.maxX - output.minX, 0, 0),
        vec3.fromValues(0, output.maxY - output.minY, 0),
        vec3.fromValues(0, 0, output.maxZ - output.minZ),
    ];
    const outputOrigin = vec3.fromValues(output.minX, output.minY, output.minZ);

    const axes: vec3[] = [];
    const pairs: [vec3, vec3][] = [
        [source.edges[0], source.edges[1]],
        [source.edges[1], source.edges[2]],
        [source.edges[2], source.edges[0]],
        [outputEdges[0], outputEdges[1]],
        [outputEdges[1], outputEdges[2]],
        [outputEdges[2], outputEdges[0]],
    ];
    for (const [a, b] of pairs) {
        axes.push(vec3.cross(vec3.create(), a, b));
    }
    for (const a of source.edges) {
        for (const b of outputEdges) {
            axes.push(vec3.cross(vec3.create(), a, b));
        }
    }

    for (const axis of axes) {
        if (vec3.squaredLength(axis) < 1e-12) {
            // Parallel edges give no separating direction of their own.
            continue;
        }
        const sourceSpan = projectParallelepiped(
            source.origin,
            source.edges,
            axis
        );
        const outputSpan = projectParallelepiped(
            outputOrigin,
            outputEdges,
            axis
        );
        if (
            sourceSpan.max < outputSpan.min ||
            outputSpan.max < sourceSpan.min
        ) {
            return false;
        }
    }
    return true;
}

/** Extent of a parallelepiped's eight corners projected onto `axis`. */
function projectParallelepiped(
    origin: vec3,
    edges: readonly vec3[],
    axis: vec3
): { min: number; max: number } {
    const base = vec3.dot(origin, axis);
    // Each edge either adds its projection or does not, so the extreme
    // corners take every positive contribution and none, respectively.
    let min = base;
    let max = base;
    for (const edge of edges) {
        const d = vec3.dot(edge, axis);
        if (d > 0) {
            max += d;
        } else {
            min += d;
        }
    }
    return { min, max };
}
